import { PropsWithChildren, useEffect, useState, useRef, useCallback, MouseEvent } from "react";
import { createPortal } from "react-dom";
import { Link as BaseLink } from "react-router-dom";
import { Text, Link } from "@jsq/ds/core";
import {
  backdropHiding,
  backdropShowing,
  drawerHeader,
  drawerHeaderTitle,
  drawerHidingFromBottom,
  pageContainerShowing,
  pageContainerHiding,
  drawerShowingFromBottom,
  drawerInner,
  drawerBodyScrollable,
  navigationLabelStyle,
} from "./styles_vanilla.css";

type Props = {
  isShowing: boolean;
  headerText?: string;
  navigationLabel?: string;
  customSectionId?: string;
  onCloseUrl?: string;
  onBeforeClose?: () => boolean;
};

export const BottomUpDrawer = ({
  isShowing,
  headerText,
  navigationLabel,
  children,
  onCloseUrl,
  onBeforeClose,
}: PropsWithChildren<Props>) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleStartClosing = useCallback(async () => {
    if (!isClosing) {
      setIsClosing(true);
      await new Promise<void>((resolve) => setTimeout(resolve, 200));
      setIsClosing(false);
    }
  }, [isClosing]);

  // Prevent page snap/shake by compensating for scrollbar space
  useEffect(() => {
    if (isShowing) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isShowing]);

  /**
   * Allow smooth closing of the modal based on the
   * changing of the isShowing prop.
   */
  const previousIsShowingRef = useRef(false);
  useEffect(() => {
    if (previousIsShowingRef.current && !isShowing) {
      void handleStartClosing();
    }
    previousIsShowingRef.current = isShowing;
  }, [handleStartClosing, isShowing]);

  const isDrawerShowingOrClosing = isShowing || isClosing;

  const handleCloseRequest = useCallback(
    (e: MouseEvent) => {
      if (onBeforeClose && !onBeforeClose()) {
        e.preventDefault();
        return;
      }
      void handleStartClosing();
    },
    [onBeforeClose, handleStartClosing],
  );

  return createPortal(
    <div
      className={isDrawerShowingOrClosing ? pageContainerShowing : pageContainerHiding}
      aria-hidden={isDrawerShowingOrClosing ? "false" : "true"}
    >
      {onCloseUrl && (
        <BaseLink to={onCloseUrl} onClick={handleCloseRequest}>
          <div className={isShowing ? backdropShowing : backdropHiding} />
        </BaseLink>
      )}
      <div className={isShowing ? drawerShowingFromBottom : drawerHidingFromBottom}>
        <div className={drawerInner}>
          <div className={drawerHeader}>
            {onCloseUrl && (
              <Link
                variant="muted"
                displayAsButton
                icon="arrow-left"
                to={onCloseUrl}
                onClick={handleCloseRequest}
                className={navigationLabelStyle}
              >
                {navigationLabel || "Back"}
              </Link>
            )}
            <Text variant="h4" className={drawerHeaderTitle}>
              {headerText}
            </Text>
            <div />
          </div>
          <div className={drawerBodyScrollable}>{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
